/**
 * KitUploadProgressDialog — opens the moment the admin picks a kit PDF
 * and stays open while the backend OCRs each block, showing live
 * progress (current employee, % bar, lists of matched / not-found).
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../App";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import {
  CheckCircle2, AlertCircle, Loader2, X, FileText,
} from "lucide-react";

const POLL_MS = 1200;

export default function KitUploadProgressDialog({
  open, onOpenChange, uploadId, adminToken, onFinished,
}) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const pollRef = useRef(null);

  useEffect(() => {
    if (!open || !uploadId) return;
    setStatus(null);
    setError("");
    const tick = async () => {
      try {
        const r = await axios.get(
          `${API}/admin/admission-kits/upload/${uploadId}/status`,
          { headers: { Authorization: `Bearer ${adminToken}` }, timeout: 8000 },
        );
        setStatus(r.data);
        if (r.data?.status === "ready") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (onFinished) onFinished(r.data);
        }
      } catch (e) {
        setError(e?.response?.data?.detail || "Falha ao consultar status");
      }
    };
    tick();
    pollRef.current = setInterval(tick, POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, uploadId, adminToken]);

  const total = status?.total_blocks || 0;
  const done = status?.processed_count || 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const isDone = status?.status === "ready";
  const matched = status?.matched_recent || [];
  const notFound = status?.not_found_recent || [];

  return (
    <Dialog open={open} onOpenChange={(v) => {
      // Allow close only when done (otherwise the OCR continues in BG anyway
      // — but a "minimize" feels cleaner).
      if (!v && !isDone) return;
      onOpenChange(v);
    }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isDone
              ? <><CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  Kit Admissional processado</>
              : <><Loader2 className="w-5 h-5 animate-spin text-sky-600" />
                  Processando Kit Admissional</>}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {error && (
            <div className="rounded-md border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-700">
              {error}
            </div>
          )}

          {!status && !error && (
            <div className="text-center py-8 text-sm text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Iniciando processamento…
            </div>
          )}

          {status && (
            <>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs text-slate-600">
                  <span>
                    {isDone ? "Concluído" : "Em andamento"} — {done} de{" "}
                    {total || "?"} blocos
                  </span>
                  <span className="font-semibold text-slate-900">
                    {pct}%
                  </span>
                </div>
                <Progress value={pct} className="h-2" />
              </div>

              {status.current_step && !isDone && (
                <div className="rounded-md border border-sky-200 bg-sky-50 p-2.5 text-xs text-sky-800 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  {status.current_step}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2.5">
                  <div className="flex items-center gap-2 text-xs font-semibold text-emerald-800 mb-1">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Identificados ({status.matched_count})
                  </div>
                  <div className="space-y-1 max-h-44 overflow-y-auto">
                    {matched.length === 0 ? (
                      <div className="text-[11px] text-emerald-700/70">
                        Nenhum até o momento…
                      </div>
                    ) : (
                      matched.slice().reverse().map((m, i) => (
                        <div key={i}
                          className="text-[11px] text-emerald-900 flex items-start gap-1">
                          <span className="font-mono">{m.matricula || "—"}</span>
                          <span className="truncate">{m.nome || "?"}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5">
                  <div className="flex items-center gap-2 text-xs font-semibold text-amber-800 mb-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    Não localizados ({status.not_found_count})
                  </div>
                  <div className="space-y-1 max-h-44 overflow-y-auto">
                    {notFound.length === 0 ? (
                      <div className="text-[11px] text-amber-700/70">
                        Nenhum até o momento…
                      </div>
                    ) : (
                      notFound.slice().reverse().map((n, i) => (
                        <div key={i}
                          className="text-[11px] text-amber-900 flex items-start gap-1">
                          <FileText className="w-2.5 h-2.5 mt-0.5 flex-shrink-0" />
                          <span className="truncate">{n.reason || n.filename}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          {isDone ? (
            <Button onClick={() => onOpenChange(false)}
                    data-testid="kit-progress-close">
              Fechar
            </Button>
          ) : (
            <Button variant="outline" disabled className="opacity-70">
              <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
              Aguarde…
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
